import React, { useEffect, useRef, useState } from "react";
import { Grid, Button, Typography, Box } from "@mui/material";
import Layout from "../../layout/Layout";
import {
  config,
  invoiceItemsAPI,
  manageInvoiceAPI,
  manageInvoiceTermsAndConditionsAPI,
  projectMasterAPI,
} from "../../config";
import axios from "axios";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { useParams } from "react-router-dom";
import { ToWords } from "to-words";
import moment from "moment";
import "moment/locale/en-in";
import generatePDF, { Margin } from "react-to-pdf";
import DOMPurify from "dompurify";

const toWords = new ToWords({
  localeCode: "en-IN",
  converterOptions: {
    currency: false,
    ignoreDecimal: true,
    ignoreZeroCurrency: false,
    doNotAddOnly: false,
  },
});

const parseNum = (val) => {
  if (val === null || val === undefined || val === "") return 0;
  const n = parseFloat(val);
  return isNaN(n) ? 0 : n;
};

const formatAmount = (val) => {
  const n = parseNum(val);
  return n.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const convertToWordsINR = (val) => {
  const num = Math.round(parseNum(val));
  if (num <= 0) return "INR Zero Only";
  try {
    const rawWords = toWords.convert(num, { currency: false });
    const words = rawWords
      .replace(/-/g, " ")
      .split(/\s+/)
      .filter(Boolean)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
      .join(" ");
    return `INR ${words} Only`;
  } catch (err) {
    return `INR ${num} Only`;
  }
};

function InvoiceView() {
  const { poid } = useParams();
  const ref = useRef();

  const downloadPDF = () => {
    generatePDF(ref, {
      filename: `Invoice-${poid}.pdf`,
      page: {
        margin: Margin.MEDIUM,
      },
    });
  };

  const [poDetails, setPODetails] = useState(null);
  const [paymentList, setPaymentList] = useState([]);
  const [storeDetails, setStoreDetails] = useState({});

  // Fetch Invoice Details & Store Details
  useEffect(() => {
    if (!poid) return;

    const data = {
      intType: 2,
      intInvoiceId: poid,
      intLimit: 1,
      intOffset: 0,
    };
    config.method = "POST";
    config.data = JSON.stringify(data);
    config.url = manageInvoiceAPI;

    axios(config)
      .then(function (response) {
        if (response.data && response.data.result && response.data.result.length > 0) {
          const detail = response.data.result[0];
          setPODetails(detail);

          if (detail.store_name && parseInt(detail.store_name) > 0) {
            const storeData = {
              intType: 2,
              intProjectID: detail.store_name,
              intLimit: 1,
            };
            config.method = "POST";
            config.data = JSON.stringify(storeData);
            config.url = projectMasterAPI;
            axios(config)
              .then((storeRes) => {
                if (storeRes.data && storeRes.data.result && storeRes.data.result.length > 0) {
                  setStoreDetails(storeRes.data.result[0]);
                }
              })
              .catch((err) => console.error("Store fetch error:", err));
          }
        }
      })
      .catch(function (error) {
        console.error("Invoice fetch error:", error);
      });
  }, [poid]);

  // Fetch Invoice Items
  useEffect(() => {
    if (!poid) return;

    const data = {
      intIONo: parseInt(poid),
      intType: 2,
      intLimit: 50,
      intOffset: 0,
    };
    config.method = "POST";
    config.data = JSON.stringify(data);
    config.url = invoiceItemsAPI;

    axios(config)
      .then(function (response) {
        if (response.data && response.data.result) {
          setPaymentList(response.data.result);
        }
      })
      .catch(function (error) {
        console.error("Invoice items fetch error:", error);
      });
  }, [poid]);

  const renderHTML = (htmlString) => {
    return { __html: DOMPurify.sanitize(htmlString || "") };
  };

  // Calculations
  const computedTotalWithoutGst =
    paymentList && paymentList.length > 0
      ? paymentList.reduce(
          (sum, item) =>
            sum +
            parseNum(
              item.total_amount_without_gst ||
                parseNum(item.quantity) * parseNum(item.rate)
            ),
          0
        )
      : parseNum(poDetails?.total_without_gst);

  const computedTotalCgst =
    paymentList && paymentList.length > 0
      ? paymentList.reduce((sum, item) => sum + parseNum(item.cgst), 0)
      : parseNum(poDetails?.total_cgst);

  const computedTotalSgst =
    paymentList && paymentList.length > 0
      ? paymentList.reduce((sum, item) => sum + parseNum(item.sgst), 0)
      : parseNum(poDetails?.total_sgst);

  const computedTotalIgst =
    paymentList && paymentList.length > 0
      ? paymentList.reduce((sum, item) => sum + parseNum(item.igst), 0)
      : parseNum(poDetails?.total_igst);

  const computedTotalTax = computedTotalCgst + computedTotalSgst + computedTotalIgst;

  const computedTotalWithGst =
    paymentList && paymentList.length > 0
      ? paymentList.reduce(
          (sum, item) =>
            sum +
            (parseNum(item.total_amount_with_gst) ||
              parseNum(item.total_amount_without_gst) +
                parseNum(item.cgst) +
                parseNum(item.sgst) +
                parseNum(item.igst)),
          0
        )
      : parseNum(poDetails?.total_with_gst) ||
        computedTotalWithoutGst + computedTotalTax;

  // HSN Tax Breakdown
  const hsnMap = {};
  if (paymentList && paymentList.length > 0) {
    paymentList.forEach((item) => {
      const code = item.hsn_code || poDetails?.hsn_sac || "995419";
      if (!hsnMap[code]) {
        hsnMap[code] = {
          hsn_code: code,
          taxableValue: 0,
          cgstAmount: 0,
          sgstAmount: 0,
          igstAmount: 0,
          gstRate: parseNum(item.gst),
        };
      }
      hsnMap[code].taxableValue += parseNum(
        item.total_amount_without_gst ||
          parseNum(item.quantity) * parseNum(item.rate)
      );
      hsnMap[code].cgstAmount += parseNum(item.cgst);
      hsnMap[code].sgstAmount += parseNum(item.sgst);
      hsnMap[code].igstAmount += parseNum(item.igst);
    });
  } else if (poDetails) {
    const code = poDetails.hsn_sac || "995419";
    hsnMap[code] = {
      hsn_code: code,
      taxableValue: computedTotalWithoutGst,
      cgstAmount: computedTotalCgst,
      sgstAmount: computedTotalSgst,
      igstAmount: computedTotalIgst,
      gstRate: 18,
    };
  }
  const hsnRows = Object.values(hsnMap);

  return (
    <Layout>
      <Grid className="login-text-background list_advance">
        <Typography variant="h5" textAlign="center">
          Invoice View
        </Typography>
      </Grid>

      <Grid container spacing={2} className="bg-white">
        <Grid item xs={12}>
          <Grid className="element-to-po" ref={ref}>
            <style>{`
              .tax-invoice-box {
                width: 100%;
                max-width: 800px;
                margin: 0 auto;
                border: 1px solid #000;
                font-family: Arial, Helvetica, sans-serif;
                font-size: 10px;
                color: #000;
                background-color: #fff;
                box-sizing: border-box;
              }
              .tax-invoice-box table {
                width: 100%;
                border-collapse: collapse;
                border-spacing: 0;
              }
              .tax-invoice-box th, .tax-invoice-box td {
                border: 1px solid #000;
                padding: 3px 5px;
                vertical-align: top;
                font-size: 10px;
                line-height: 1.3;
                box-sizing: border-box;
                word-break: break-word;
              }
              .tax-invoice-box .text-center {
                text-align: center;
              }
              .tax-invoice-box .text-right {
                text-align: right;
              }
              .tax-invoice-box .text-bold {
                font-weight: bold;
              }
              .tax-invoice-box .title-header {
                font-size: 13px;
                font-weight: bold;
                text-align: center;
                padding: 4px;
                border-bottom: 1px solid #000;
              }
              .tax-invoice-box .sub-info-table td {
                border: none;
                padding: 2px 4px;
              }
              @media print {
                body {
                  margin: 0;
                  padding: 0;
                }
                .tax-invoice-box {
                  border: 1px solid #000 !important;
                  width: 100% !important;
                  max-width: 100% !important;
                }
                .element-to-po {
                  padding: 0 !important;
                  margin: 0 !important;
                }
                @page {
                  size: A4;
                  margin: 10mm;
                }
              }
            `}</style>

            <div className="tax-invoice-box">
              <div className="title-header">Tax Invoice</div>

              {/* Header Info Table (Seller/Consignee/Buyer vs Invoice Metadata) */}
              <table style={{ borderTop: "none", borderLeft: "none", borderRight: "none" }}>
                <tbody>
                  <tr>
                    {/* Left Column: Seller, Consignee, Buyer (50% width) */}
                    <td style={{ width: "50%", padding: 0, borderLeft: "none", borderTop: "none" }}>
                      {/* Seller Block */}
                      <div style={{ padding: "4px 6px", borderBottom: "1px solid #000", minHeight: "82px" }}>
                        <div className="text-bold" style={{ fontSize: "11px" }}>
                          {poDetails?.company_name || "K.G.BUILD TECH"}
                        </div>
                        <div>
                          {poDetails?.address || "KG Nest, Plot No. 108, Ground Floor, Perumal Koil Street, Alapakkam, Chennai - 600116."}
                        </div>
                        <div className="text-bold" style={{ marginTop: "2px" }}>
                          GSTIN/UIN: {poDetails?.gst_no || "33AVJPG9825D1ZN"}
                        </div>
                        <div>
                          State Name : {poDetails?.state || "Tamil Nadu"}, Code : {poDetails?.state?.toLowerCase()?.includes("tamil") ? "33" : "33"}
                        </div>
                      </div>

                      {/* Consignee Block */}
                      <div style={{ padding: "4px 6px", borderBottom: "1px solid #000", minHeight: "95px" }}>
                        <div className="text-bold">Consignee (Ship to)</div>
                        <div className="text-bold" style={{ fontSize: "11px" }}>
                          {poDetails?.client_name || "ADITYA BIRLA LIFESTYLE BRANDS LIMITED"}
                        </div>
                        <div>
                          {storeDetails?.project_address || storeDetails?.name || poDetails?.client_address || "VAN HEUSEN, PLOT NO.3, S NO 256 , GF, KEELKATTALAI VILLAGE, MADIPAKKAM MAIN ROAD, ARUNACHALA NAGAR, CHENNAI - 600117."}
                        </div>
                        {storeDetails?.code && <div>Store Code : {storeDetails.code}</div>}
                        <div style={{ marginTop: "2px" }}>
                          GSTIN/UIN : {poDetails?.client_gst || "33ABACA5076G1ZX"}
                        </div>
                        <div>
                          State Name : {poDetails?.client_state || "Tamil Nadu"}, Code : 33
                        </div>
                      </div>

                      {/* Buyer Block */}
                      <div style={{ padding: "4px 6px", minHeight: "95px" }}>
                        <div className="text-bold">Buyer (Bill to)</div>
                        <div className="text-bold" style={{ fontSize: "11px" }}>
                          {poDetails?.client_name || "ADITYA BIRLA LIFESTYLE BRANDS LIMITED"}
                        </div>
                        <div>
                          {poDetails?.client_address || "Louis Philippe, old No.83/ 1, originally 1/13 and new N0.8, Thyagaraya Road, T. Nagar, Chennai - 600017."}
                        </div>
                        <div>
                          {poDetails?.client_city ? `${poDetails.client_city} - ${poDetails.client_pincode || ""}` : ""}
                        </div>
                        <div style={{ marginTop: "2px" }}>
                          GSTIN/UIN : {poDetails?.client_gst || "33ABACA5076G1ZX"}
                        </div>
                        <div>
                          State Name : {poDetails?.client_state || "Tamil Nadu"}, Code : 33
                        </div>
                      </div>
                    </td>

                    {/* Right Column: Invoice Metadata Grid (50% width) */}
                    <td style={{ width: "50%", padding: 0, borderRight: "none", borderTop: "none" }}>
                      <table style={{ width: "100%", height: "100%", border: "none" }}>
                        <tbody>
                          <tr style={{ borderBottom: "1px solid #000" }}>
                            <td style={{ width: "50%", borderTop: "none", borderLeft: "none", borderRight: "1px solid #000" }}>
                              <div>Invoice No.</div>
                              <div className="text-bold">{poDetails?.invoice_no || "KGBT/160/26-27"}</div>
                            </td>
                            <td style={{ width: "50%", borderTop: "none", borderRight: "none" }}>
                              <div>Dated</div>
                              <div className="text-bold">
                                {poDetails?.createdAt || poDetails?.created_at
                                  ? moment(poDetails.createdAt || poDetails.created_at).format("D-MMM-YY")
                                  : moment().format("D-MMM-YY")}
                              </div>
                            </td>
                          </tr>
                          <tr style={{ borderBottom: "1px solid #000" }}>
                            <td style={{ borderLeft: "none", borderRight: "1px solid #000" }}>
                              <div>Delivery Note</div>
                              <div className="text-bold">{poDetails?.delivery_note || ""}</div>
                            </td>
                            <td style={{ borderRight: "none" }}>
                              <div>Mode/Terms of Payment</div>
                              <div className="text-bold">{poDetails?.terms_of_payments || ""}</div>
                            </td>
                          </tr>
                          <tr style={{ borderBottom: "1px solid #000" }}>
                            <td style={{ borderLeft: "none", borderRight: "1px solid #000" }}>
                              <div>Reference No. & Date.</div>
                              <div className="text-bold">{poDetails?.reference_no || ""}</div>
                            </td>
                            <td style={{ borderRight: "none" }}>
                              <div>Other References</div>
                              <div className="text-bold">{poDetails?.other_references || ""}</div>
                            </td>
                          </tr>
                          <tr style={{ borderBottom: "1px solid #000" }}>
                            <td style={{ borderLeft: "none", borderRight: "1px solid #000" }}>
                              <div>Buyer's Order No.</div>
                              <div className="text-bold">
                                {poDetails?.po_ref_no
                                  ? poDetails.po_ref_no.startsWith("Po No.")
                                    ? poDetails.po_ref_no
                                    : `Po No. ${poDetails.po_ref_no}`
                                  : ""}
                              </div>
                            </td>
                            <td style={{ borderRight: "none" }}>
                              <div>Dated</div>
                              <div className="text-bold">
                                {poDetails?.po_date ? moment(poDetails.po_date).format("D-MMM-YY") : ""}
                              </div>
                            </td>
                          </tr>
                          <tr style={{ borderBottom: "1px solid #000" }}>
                            <td style={{ borderLeft: "none", borderRight: "1px solid #000" }}>
                              <div>Dispatch Doc No.</div>
                              <div className="text-bold">{poDetails?.dispatch_doc_no || ""}</div>
                            </td>
                            <td style={{ borderRight: "none" }}>
                              <div>Delivery Note Date</div>
                              <div className="text-bold">{poDetails?.delivery_note_date || ""}</div>
                            </td>
                          </tr>
                          <tr style={{ borderBottom: "1px solid #000" }}>
                            <td style={{ borderLeft: "none", borderRight: "1px solid #000" }}>
                              <div>Dispatched through</div>
                              <div className="text-bold">{poDetails?.dispatched_through || ""}</div>
                            </td>
                            <td style={{ borderRight: "none" }}>
                              <div>Destination</div>
                              <div className="text-bold">{poDetails?.destination || ""}</div>
                            </td>
                          </tr>
                          <tr>
                            <td colSpan={2} style={{ borderLeft: "none", borderRight: "none", borderBottom: "none" }}>
                              <div>Terms of Delivery</div>
                              <div style={{ minHeight: "35px" }}>{poDetails?.terms_of_delivery || ""}</div>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </td>
                  </tr>
                </tbody>
              </table>

              {/* Line Items Table */}
              <table style={{ borderLeft: "none", borderRight: "none", borderBottom: "none" }}>
                <thead>
                  <tr>
                    <th style={{ width: "5%", borderLeft: "none", borderTop: "1px solid #000" }} className="text-center text-bold">Sl No.</th>
                    <th style={{ width: "45%", borderTop: "1px solid #000" }} className="text-center text-bold">Particulars</th>
                    <th style={{ width: "10%", borderTop: "1px solid #000" }} className="text-center text-bold">HSN/SAC</th>
                    <th style={{ width: "10%", borderTop: "1px solid #000" }} className="text-center text-bold">Quantity</th>
                    <th style={{ width: "10%", borderTop: "1px solid #000" }} className="text-center text-bold">Rate</th>
                    <th style={{ width: "5%", borderTop: "1px solid #000" }} className="text-center text-bold">per</th>
                    <th style={{ width: "15%", borderRight: "none", borderTop: "1px solid #000" }} className="text-center text-bold">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {paymentList && paymentList.length > 0 ? (
                    paymentList.map((row, index) => {
                      const itemCgst = parseNum(row.cgst);
                      const itemSgst = parseNum(row.sgst);
                      const itemIgst = parseNum(row.igst);
                      const baseAmt = parseNum(row.total_amount_without_gst) || parseNum(row.quantity) * parseNum(row.rate);

                      return (
                        <React.Fragment key={index}>
                          <tr style={{ borderBottom: "none" }}>
                            <td className="text-center" style={{ borderLeft: "none", borderBottom: "none" }}>{index + 1}</td>
                            <td style={{ borderBottom: "none" }}>
                              <div className="text-bold" style={{ textTransform: "uppercase" }}>{row.item_name}</div>
                              {row.item_description && (
                                <div style={{ fontStyle: "italic", fontSize: "9px", marginTop: "2px" }}>
                                  {row.item_description}
                                </div>
                              )}
                              {itemCgst > 0 && <div className="text-bold" style={{ marginTop: "4px" }}>CGST</div>}
                              {itemSgst > 0 && <div className="text-bold">SGST</div>}
                              {itemIgst > 0 && <div className="text-bold">IGST</div>}
                            </td>
                            <td className="text-center" style={{ borderBottom: "none" }}>{row.hsn_code || poDetails?.hsn_sac || "995419"}</td>
                            <td className="text-right" style={{ borderBottom: "none" }}>{row.quantity ? formatAmount(row.quantity) : ""}</td>
                            <td className="text-right" style={{ borderBottom: "none" }}>{row.rate ? formatAmount(row.rate) : ""}</td>
                            <td className="text-center" style={{ borderBottom: "none" }}>{row.unit_name || row.unit || ""}</td>
                            <td className="text-right text-bold" style={{ borderRight: "none", borderBottom: "none" }}>
                              <div>{formatAmount(baseAmt)}</div>
                              {itemCgst > 0 && <div style={{ marginTop: "4px" }}>{formatAmount(itemCgst)}</div>}
                              {itemSgst > 0 && <div>{formatAmount(itemSgst)}</div>}
                              {itemIgst > 0 && <div>{formatAmount(itemIgst)}</div>}
                            </td>
                          </tr>
                        </React.Fragment>
                      );
                    })
                  ) : (
                    <tr>
                      <td className="text-center" style={{ borderLeft: "none", borderBottom: "none" }}>1</td>
                      <td style={{ borderBottom: "none" }}>
                        <div className="text-bold">CIVIL & INTERIOR WORKS</div>
                        <div style={{ fontStyle: "italic", fontSize: "9px" }}>Supply of Back Office Fan at Van Heusen Madipakkam.</div>
                        <div className="text-bold" style={{ marginTop: "4px" }}>CGST</div>
                        <div className="text-bold">SGST</div>
                      </td>
                      <td className="text-center" style={{ borderBottom: "none" }}>995419</td>
                      <td className="text-right" style={{ borderBottom: "none" }}></td>
                      <td className="text-right" style={{ borderBottom: "none" }}></td>
                      <td className="text-center" style={{ borderBottom: "none" }}></td>
                      <td className="text-right text-bold" style={{ borderRight: "none", borderBottom: "none" }}>
                        <div>3,850.00</div>
                        <div style={{ marginTop: "4px" }}>346.50</div>
                        <div>346.50</div>
                      </td>
                    </tr>
                  )}

                  {/* Empty Spacer Row for Vertical Lines */}
                  <tr>
                    <td style={{ borderLeft: "none", borderBottom: "none", height: "100px" }}></td>
                    <td style={{ borderBottom: "none" }}></td>
                    <td style={{ borderBottom: "none" }}></td>
                    <td style={{ borderBottom: "none" }}></td>
                    <td style={{ borderBottom: "none" }}></td>
                    <td style={{ borderBottom: "none" }}></td>
                    <td style={{ borderRight: "none", borderBottom: "none" }}></td>
                  </tr>

                  {/* Total Row */}
                  <tr style={{ borderTop: "1px solid #000", borderBottom: "1px solid #000" }}>
                    <td colSpan={6} className="text-right text-bold" style={{ borderLeft: "none", borderRight: "1px solid #000" }}>
                      Total
                    </td>
                    <td className="text-right text-bold" style={{ borderRight: "none" }}>
                      ₹ {formatAmount(computedTotalWithGst)}
                    </td>
                  </tr>
                </tbody>
              </table>

              {/* Amount Chargeable in words */}
              <div style={{ borderBottom: "1px solid #000", padding: "4px 6px" }}>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span>Amount Chargeable (in words)</span>
                  <span style={{ fontStyle: "italic" }}>E. & O.E</span>
                </div>
                <div className="text-bold" style={{ fontSize: "11px", marginTop: "2px" }}>
                  {convertToWordsINR(computedTotalWithGst)}
                </div>
              </div>

              {/* HSN/SAC Tax Breakdown Table */}
              <table style={{ borderLeft: "none", borderRight: "none", borderBottom: "1px solid #000" }}>
                <thead>
                  <tr>
                    <th style={{ width: "15%", borderLeft: "none", borderTop: "none" }} className="text-center text-bold" rowSpan={2}>HSN/SAC</th>
                    <th style={{ width: "20%", borderTop: "none" }} className="text-center text-bold" rowSpan={2}>Taxable Value</th>
                    <th style={{ width: "22.5%", borderTop: "none" }} className="text-center text-bold" colSpan={2}>CGST</th>
                    <th style={{ width: "22.5%", borderTop: "none" }} className="text-center text-bold" colSpan={2}>SGST/UTGST</th>
                    <th style={{ width: "20%", borderRight: "none", borderTop: "none" }} className="text-center text-bold" rowSpan={2}>Total Tax Amount</th>
                  </tr>
                  <tr>
                    <th style={{ borderTop: "1px solid #000" }} className="text-center text-bold">Rate</th>
                    <th style={{ borderTop: "1px solid #000" }} className="text-center text-bold">Amount</th>
                    <th style={{ borderTop: "1px solid #000" }} className="text-center text-bold">Rate</th>
                    <th style={{ borderTop: "1px solid #000" }} className="text-center text-bold">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {hsnRows.map((hsn, idx) => {
                    const halfRate = hsn.gstRate ? `${hsn.gstRate / 2}%` : "9%";
                    return (
                      <tr key={idx}>
                        <td className="text-center" style={{ borderLeft: "none" }}>{hsn.hsn_code}</td>
                        <td className="text-right">{formatAmount(hsn.taxableValue)}</td>
                        <td className="text-center">{halfRate}</td>
                        <td className="text-right">{formatAmount(hsn.cgstAmount)}</td>
                        <td className="text-center">{halfRate}</td>
                        <td className="text-right">{formatAmount(hsn.sgstAmount)}</td>
                        <td className="text-right text-bold" style={{ borderRight: "none" }}>
                          {formatAmount(hsn.cgstAmount + hsn.sgstAmount + hsn.igstAmount)}
                        </td>
                      </tr>
                    );
                  })}
                  <tr style={{ borderTop: "1px solid #000" }}>
                    <td className="text-center text-bold" style={{ borderLeft: "none" }}>Total</td>
                    <td className="text-right text-bold">{formatAmount(computedTotalWithoutGst)}</td>
                    <td></td>
                    <td className="text-right text-bold">{formatAmount(computedTotalCgst)}</td>
                    <td></td>
                    <td className="text-right text-bold">{formatAmount(computedTotalSgst)}</td>
                    <td className="text-right text-bold" style={{ borderRight: "none" }}>
                      {formatAmount(computedTotalTax)}
                    </td>
                  </tr>
                </tbody>
              </table>

              {/* Tax Amount in Words */}
              <div style={{ borderBottom: "1px solid #000", padding: "4px 6px" }}>
                Tax Amount (in words) : <span className="text-bold">{convertToWordsINR(computedTotalTax)}</span>
              </div>

              {/* Bank Details & Signature Section */}
              <table style={{ borderLeft: "none", borderRight: "none", borderBottom: "none" }}>
                <tbody>
                  <tr>
                    {/* Bank Details (Left 60%) */}
                    <td style={{ width: "60%", borderLeft: "none", borderTop: "none", borderBottom: "none", borderRight: "1px solid #000", padding: "6px" }}>
                      <div className="text-bold" style={{ textDecoration: "underline" }}>Company's Bank Details</div>
                      <table style={{ width: "100%", marginTop: "3px", border: "none" }}>
                        <tbody>
                          <tr>
                            <td className="text-bold" style={{ width: "35%", border: "none", padding: "1px 0" }}>A/c Holder's Name</td>
                            <td className="text-bold" style={{ border: "none", padding: "1px 0" }}>
                              : {poDetails?.account_name || poDetails?.company_name || "K G BUILD TECH"}
                            </td>
                          </tr>
                          <tr>
                            <td className="text-bold" style={{ border: "none", padding: "1px 0" }}>Bank Name</td>
                            <td style={{ border: "none", padding: "1px 0" }}>
                              : {poDetails?.bank_name || "Karur Vysya Bank A/c"}
                            </td>
                          </tr>
                          <tr>
                            <td className="text-bold" style={{ border: "none", padding: "1px 0" }}>A/c No.</td>
                            <td className="text-bold" style={{ border: "none", padding: "1px 0" }}>
                              : {poDetails?.account_number || "1264135000007979"}
                            </td>
                          </tr>
                          <tr>
                            <td className="text-bold" style={{ border: "none", padding: "1px 0" }}>Branch & IFS Code</td>
                            <td style={{ border: "none", padding: "1px 0" }}>
                              : {poDetails?.branch
                                ? `${poDetails.branch} & ${poDetails.ifsc_code || ""}`
                                : "Valasaravakkam & KVBL0001264"}
                            </td>
                          </tr>
                        </tbody>
                      </table>

                      {poDetails?.invoice_terms_description && (
                        <div style={{ marginTop: "8px" }}>
                          <span className="text-bold">Remarks/Terms:</span>
                          <div dangerouslySetInnerHTML={renderHTML(poDetails.invoice_terms_description)} />
                        </div>
                      )}
                    </td>

                    {/* Signatures (Right 40%) */}
                    <td style={{ width: "40%", borderRight: "none", borderTop: "none", borderBottom: "none", padding: 0 }}>
                      <table style={{ width: "100%", height: "100%", border: "none" }}>
                        <tbody>
                          <tr>
                            <td className="text-center" style={{ height: "45px", borderTop: "none", borderLeft: "none", borderRight: "none", borderBottom: "1px solid #000" }}>
                              <div>Customer's Seal and Signature</div>
                            </td>
                          </tr>
                          <tr>
                            <td className="text-center" style={{ height: "75px", borderBottom: "none", borderLeft: "none", borderRight: "none", verticalAlign: "bottom", paddingBottom: "6px" }}>
                              <div>for <span className="text-bold">{poDetails?.company_name || "K.G.BUILD TECH"}</span></div>
                              <div style={{ height: "35px" }}></div>
                              <div className="text-bold">Authorised Signatory</div>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </td>
                  </tr>
                </tbody>
              </table>

              {/* Computer Generated Caption */}
              <div className="text-center" style={{ borderTop: "1px solid #000", padding: "3px", fontSize: "9px" }}>
                This is a Computer Generated Invoice
              </div>
            </div>
          </Grid>

          <div className="empty-space-20"></div>
        </Grid>

        <Grid item xs={12}>
          <Box display="flex" justifyContent="flex-end" mr={2} mb={2}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              startIcon={<PictureAsPdfIcon />}
              onClick={downloadPDF}
              sx={{
                backgroundColor: "#2196f3",
                fontWeight: "bold",
                transition: "background-color 0.3s ease, box-shadow 0.3s ease",
                "&:hover": {
                  backgroundColor: "#FFF",
                  color: "#2196f3",
                  boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
                },
              }}
            >
              Generate PDF
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Layout>
  );
}

export default InvoiceView;
